import { formatDate } from "date-fns";
import { download, generateCsv } from "export-to-csv";

import { ContactsAnalyticsService } from "@analytics/index";

import { getContactEmails } from "@api/portfolio.api";

import { ContactType } from "@interfaces/contact.type";

import { PopupActions } from "@store/popupStore";

import { csvExportConfig, getValueOrDefault } from "@utilities/tableExport";

interface IContactFilters {
  hasAttendedConference: boolean;
  hasVerifiedEmail: boolean;
  jobTitles: string[];
  keywords: string[];
  locations: string[];
}

/**
 * Filters a contact based on job titles.
 *
 * @param contact - The contact object to filter.
 * @param jobTitles - An array of job titles to filter by.
 * @returns A boolean indicating whether the contact matches the job titles.
 */
const jobTitleFilter = (contact: ContactType, jobTitles: string[]) => {
  if (jobTitles.length === 0) return true;
  if (!contact.title) return false;
  const titleLowerCase = contact.title.toLowerCase();
  const jobTitleFiltersLowerCase = jobTitles.map((jobTitle) => jobTitle.toLowerCase());

  return jobTitleFiltersLowerCase.every((jobTitle) => titleLowerCase.includes(jobTitle));
};

/**
 * Filters a contact based on an array of keywords.
 * @param contact - The contact object to filter.
 * @param keywords - An array of keywords to filter the contact by.
 * @returns A boolean indicating whether the contact matches all the keywords.
 */
const keywordFilter = (contact: ContactType, keywords: string[]) => {
  if (keywords.length === 0) return true;

  const targetString = JSON.stringify(contact).toLowerCase();

  return keywords.every((keyword) => targetString.includes(keyword.toLowerCase()));
};

/**
 * Filters a contact based on the provided locations.
 *
 * @param contact - The contact object to filter.
 * @param locations - An array of locations to filter by.
 * @returns A boolean indicating whether the contact matches any of the provided locations.
 */
const locationFilter = (contact: ContactType, locations: string[]) => {
  if (locations.length === 0) return true;

  const locationString = [contact.city || "", contact.state || "", contact.country || ""].join(" ").toLowerCase();

  return locations.every((location) => locationString.includes(location.toLowerCase()));
};

/**
 * Filters a contact based on the email status.
 *
 * @param contact - The contact object to filter.
 * @param verified - A boolean indicating whether to filter by verified email status.
 * @returns A boolean indicating whether the contact passes the filter.
 */
const emailStatusFilter = (contact: ContactType, verified: boolean) =>
  verified ? contact.emailStatus === "verified" : true;

/**
 * Filters a contact based on the attended conference status.
 *
 * @param contact - The contact object to filter.
 * @param hasAttendedConference - A boolean indicating whether to filter by attended conference status.
 * @returns A boolean indicating whether the contact passes the filter.
 */
const attendedConferenceFilter = (contact: ContactType, hasAttendedConference: boolean) =>
  hasAttendedConference ? contact.hasConferences : true;

/**
 * Filters an array of contacts based on the provided filters.
 *
 * @param contacts - The array of contacts to filter.
 * @param filters - The filters to apply.
 * @returns An array of indices representing the filtered contacts.
 */
const filterContacts = (contacts: ContactType[], filters: IContactFilters) => {
  const indicesToKeep: number[] = [];

  for (const [index, contact] of contacts.entries()) {
    const filtersPass = [
      jobTitleFilter(contact, filters.jobTitles),
      keywordFilter(contact, filters.keywords),
      locationFilter(contact, filters.locations),
      emailStatusFilter(contact, filters.hasVerifiedEmail),
      attendedConferenceFilter(contact, filters.hasAttendedConference),
    ];

    if (filtersPass.every(Boolean)) {
      indicesToKeep.push(index);
    }
  }

  return indicesToKeep;
};

/**
 * Assigns emails to contacts based on the provided contact IDs and updates the email and email status properties of the contacts.
 *
 * @param contactIds - An array of contact IDs.
 * @param contacts - An array of contacts.
 * @returns A promise that resolves to an array of contacts with updated email and email status properties.
 * @throws An error if there is an issue fetching the emails.
 */
export const assignEmailsToContacts = async (contactIds: string[], contacts: ContactType[]) => {
  try {
    const contactsBatch = await fetchContactEmails(contactIds);

    return contacts.map((contact) => {
      const foundContact = contactsBatch.find((updatedContact) => updatedContact.id === contact.id);
      return foundContact ? { ...contact, email: foundContact.email, emailStatus: foundContact.emailStatus } : contact;
    });
  } catch {
    throw new Error("Error fetching emails");
  }
};

/**
 * Retrieves the IDs of contacts without emails.
 *
 * @param contacts - The array of contacts to filter.
 * @returns An array of numbers representing the IDs of contacts without emails.
 */
export const getIdsOfContactsWithoutEmails = (contacts: ContactType[]) =>
  contacts.filter((contact) => contact.email === "email_not_unlocked@domain.com").map((contact) => contact.id);

/**
 * Exports selected contacts to a CSV file.
 *
 * @param selectedContactIds - An array of selected contact IDs.
 * @param contacts - An array of contact objects.
 * @returns A promise that resolves to void.
 */
const exportContacts = async (selectedContactIds: string[], contacts: ContactType[]) => {
  if (selectedContactIds.length === 0) {
    PopupActions.openAlert({ message: "You have not selected any companies to export.", severity: "error" });
    return;
  }

  try {
    let contactsToExport = contacts.filter((contact) => selectedContactIds.includes(contact.id));
    const emailsToFetch = getIdsOfContactsWithoutEmails(contactsToExport);
    const date = formatDate(new Date(), "MM-dd-yyyy");
    const customerFileName = `contact_list_${date}.csv`;
    if (emailsToFetch.length > 0) {
      contactsToExport = await assignEmailsToContacts(emailsToFetch, contactsToExport);
    }

    const exportList = contactsToExport.map((contact) => {
      /* eslint-disable perfectionist/sort-objects */ // Required for CSV export to have columns in correct order
      return {
        first_name: getValueOrDefault(contact.firstName),
        last_name: getValueOrDefault(contact.lastName),
        email: getValueOrDefault(contact.email),
        email_status: getValueOrDefault(contact.emailStatus),
        title: getValueOrDefault(contact.title),
        seniority: getValueOrDefault(contact.seniority),
        headline: getValueOrDefault(contact.headline),
        linkedin_url: getValueOrDefault(contact.linkedinUrl),
        company_name: getValueOrDefault(contact.companyName),
        company_city: getValueOrDefault(contact.companyCity),
        company_state: getValueOrDefault(contact.companyState),
        company_country: getValueOrDefault(contact.companyCountry),
        company_linkedin: getValueOrDefault(contact.companyLinkedinUrl),
        company_website: getValueOrDefault(contact.companyPrimaryDomain),
        founded_year: getValueOrDefault(contact.companyFoundedYear),
      };
    });
    /* eslint-enable perfectionist/sort-objects */

    const csv = generateCsv(csvExportConfig(customerFileName))(exportList);
    download(csvExportConfig(customerFileName))(csv);

    ContactsAnalyticsService.trackContactsDownloaded(selectedContactIds.length);
  } catch {
    PopupActions.openAlert({ message: "Error exporting contacts. Please try again in one minute.", severity: "error" });
  }
};

/**
 * Fetches contact emails for the given contact IDs.
 *
 * @param contactIds - An array of contact IDs.
 * @returns A promise that resolves to an array of contact objects.
 * @throws If there is an error fetching the contact emails.
 */
export const fetchContactEmails = async (contactIds: string[]) => {
  try {
    const fetchedEmails = await getContactEmails(contactIds);

    if (fetchedEmails.length === 0 || !fetchedEmails[0]?.email) {
      PopupActions.openAlert({
        message: "There appears to be no email for this contact. Please flag for review",
        severity: "error",
      });
      return [];
    }

    return fetchedEmails;
  } catch {
    PopupActions.openAlert({
      message: "Error fetching contact email(s). Please try again in one minute.",
      severity: "error",
    });
    return [];
  }
};

export default {
  exportContacts,
  fetchContactEmails,
  filterContacts,
};
